// Landing page for the SSO callback redirect.
//
// The backend (routes/authGoogle.ts) sends users here with the JWT in
// the URL fragment (`#token=...`) and the original return_to in the
// query string (`?return_to=...`). We:
//   1. Parse the fragment for `token=...`.
//   2. Persist the JWT to localStorage under the "token" key — the same
//      key api/mock.ts reads in getAuthHeaders() (and the Andrii-archive
//      axios interceptor used). Keeping a single key keeps both code
//      paths in sync.
//   3. Strip the fragment from the URL via history.replaceState so the
//      JWT never leaks into the back-button history or any later log.
//   4. Navigate to the validated return_to (default `/`).
//
// On any failure (no token, malformed fragment) we redirect to
// /login?error=missing_token.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { safeReturnTo, TOKEN_STORAGE_KEY } from "../api/auth";

function parseFragment(hash: string): Record<string, string> {
  // hash is "#token=...&foo=bar" or "" — strip leading "#" then parse.
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return {};
  const out: Record<string, string> = {};
  for (const part of raw.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const k = decodeURIComponent(part.slice(0, eq));
    const v = decodeURIComponent(part.slice(eq + 1));
    out[k] = v;
  }
  return out;
}

export default function AuthGoogleReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = parseFragment(window.location.hash);
    const token = fragment.token;
    const returnTo = safeReturnTo(params.get("return_to"));

    if (!token) {
      // No token in fragment: either StrictMode ran this effect a second
      // time after the first run consumed the hash, or the user
      // back-navigated here. In both cases a token already sits in
      // storage - treat it as success instead of bouncing to an error.
      let stored: string | null = null;
      try {
        stored = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      } catch {
        /* fall through to the error path */
      }
      if (stored) {
        navigate(returnTo, { replace: true });
        return;
      }
      setError("missing_token");
      navigate("/login?error=missing_token", { replace: true });
      return;
    }

    try {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch (err) {
      // localStorage can throw in incognito + quota-exceeded scenarios.
      console.error("[AuthGoogleReturn] localStorage.setItem failed", err);
      setError("storage_failed");
      navigate("/login?error=storage_failed", { replace: true });
      return;
    }

    // Strip the fragment so the JWT doesn't sit in window.location.hash.
    // history.replaceState is preferable to navigate() because it
    // doesn't add an entry to the history stack — back-button still
    // returns the user to where they came from.
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {
      /* benign in non-browser test environments */
    }

    navigate(returnTo, { replace: true });
  }, [navigate, params]);

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "var(--muted)",
          fontSize: 13,
          fontFamily: "var(--font-ui)",
        }}
      >
        {error ? "Sign-in failed. Redirecting…" : "Signing you in…"}
      </div>
    </main>
  );
}
